import { Grid } from '@mantine/core';
import { useMemo } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router';

import { EventGroupPanel } from '../../../components/global/EventPanel/EventGroupPanel.tsx';
import { EventPanel } from '../../../components/global/EventPanel/EventPanel.tsx';
import { groupEventsByGroup } from './groupEventsByGroup.ts';

type ParsedEvent = Omit<ComponentProps<typeof EventPanel>, 'onDropdownClick'> & {
  id: string;
  eventsGroup: number | null;
};

export const useEventGrid = ({
  events,
  entityType,
  basePath,
  emptyContent,
  onDropdownClick,
}: {
  events: ParsedEvent[] | undefined;
  entityType: 'event' | 'incomplete-event';
  basePath: string;
  emptyContent: ReactNode;
  onDropdownClick: (params: {
    state: 'edit' | 'remove' | 'remove-all';
    id?: string;
    entityType: 'event' | 'incomplete-event';
  }) => void;
}) =>
  useMemo(() => {
    const grouped = groupEventsByGroup(events);

    if (!grouped || (grouped.singles.length === 0 && grouped.groups.length === 0)) {
      return emptyContent;
    }

    const singleCols = grouped.singles.map((event) => {
      const { id, eventsGroup, ...props } = event;
      return (
        <Grid.Col key={id} span={{ base: 12, sm: 6, md: 4, lg: 4 }}>
          <Link style={{ textDecoration: 'none' }} to={`${basePath}/${id}/edit`}>
            <EventPanel
              {...props}
              onDropdownClick={(e, state) => {
                e.stopPropagation();
                onDropdownClick({ state, entityType, id });
              }}
            />
          </Link>
        </Grid.Col>
      );
    });

    const groupCols = grouped.groups.map(({ eventsGroup, events: groupEvents, firstDate, lastDate }) => {
      const first = groupEvents[0];
      const { imageSrc, title, tags, type, locationName, status } = first;
      return (
        <Grid.Col key={eventsGroup} span={{ base: 12, sm: 6, md: 4, lg: 4 }}>
          <EventGroupPanel
            eventCount={groupEvents.length}
            events={groupEvents.map((e) => ({ id: e.id, date: e.date, status: e.status }))}
            firstDate={firstDate}
            imageSrc={imageSrc}
            lastDate={lastDate}
            locationName={locationName}
            status={status}
            tags={tags}
            title={title}
            type={type}
            onDeleteAll={(e, id) => {
              e.stopPropagation();
              onDropdownClick({ state: 'remove-all', entityType, id });
            }}
            onDropdownClick={(e, state, id) => {
              e.stopPropagation();
              onDropdownClick({ state, entityType, id });
            }}
          />
        </Grid.Col>
      );
    });

    return [...singleCols, ...groupCols];
  }, [events, entityType, basePath, emptyContent, onDropdownClick]);
